import Avvir, { ApiElementDeviation, ApiPlannedElement, UserActionType } from "avvir";

import UndoableAction from "./undoable_action";
import getProjectId from "../../services/getters/project_getters/get_project_id";
import getUser from "../../services/getters/base_getters/get_user";
import { ActionHistoryEntry } from "../../services/reducers/reduce_action_history";
import { ElementEditedEventType, getEditElementEventTypes } from "./element_edit_action_event_types";
import camerasController from "../../services/utilities/threejs_utilities/cameras_controller";
import ensureFullPlannedElements from "../../actions/viewer_page/ensure_full_planned_elements";
import recordUserActions from "../../actions/record_user_actions";
import PlannedBuildingElement from "./planned_building_element";

import type { ElementEditedEventPayload } from "./element_edited_event";
import type { Dispatch, GetState } from "type_aliases";

export const isElementEditActionPayload = (edit: ActionHistoryEntry): edit is ElementEditActionEntry => {
  return getEditElementEventTypes().has((edit.action as ElementEditAction).type);
};

export interface ElementEditActionEntry extends ActionHistoryEntry {
  action: ElementEditAction;
}

export default class ElementEditAction extends UndoableAction {
  constructor(name: string, type: ElementEditedEventType, floorId: string) {
    super(name, ["domain", "plannedBuildingElements", "byFirebaseFloorId", floorId, "byGlobalId"]);
    this.type = type;
    this.payload = {
      floorId,
      previousElementStates: null, // This will get filled in by the child class
      nextElementStates: null, // This will get filled in by the child class
    };
  }

  type: ElementEditedEventType;
  payload: ElementEditedEventPayload;

  undo(dispatch: Dispatch, getState: GetState) {
    return this.applyElementStates(dispatch, getState, this.payload.previousElementStates);
  }

  redo(dispatch: Dispatch, getState: GetState) {
    if (this.payload.nextElementStates == null) {
      return super.redo(dispatch, getState);
    }
    return this.applyElementStates(dispatch, getState, this.payload.nextElementStates);
  }

  private async applyElementStates(dispatch: Dispatch, getState: GetState, elementStates: PlannedBuildingElement[]) {
    const projectId = getProjectId(getState(), {});
    const user = getUser(getState(), {});
    const floorId = this.payload.floorId;
    // A captured state is only as complete as the store was when the edit happened, so an edit made
    // before an element was filled in captures a minimal-payload record. This endpoint writes every
    // column it receives with no fallback to the stored row, so undo and redo have to send whole
    // elements or they would blank the element's name, type and classification.
    const fullElementStates = await ensureFullPlannedElements(projectId, floorId, elementStates, user);
    const updatedElements = fullElementStates.map((element) => {
      let pbe = new ApiPlannedElement({
        ...element,
      });
      // The gateway treats deviation being unset as "don't change this" so we
      // need to provide an empty deviation object.
      if (element.deviation == null) {
        pbe.deviation = new ApiElementDeviation();
      }
      if (element.fixedDeviation == null && pbe.fixedAt != null) {
        pbe.fixedDeviation = new ApiElementDeviation();
      }
      // TODO: This should be unnecessary once we remove the clashing boolean from the api object
      if (pbe.deviation?.deviationVectorMeters != null) {
        pbe.deviation.clashing = false;
      }
      if (pbe.fixedDeviation?.deviationVectorMeters != null) {
        pbe.fixedDeviation.clashing = false;
      }
      return pbe;
    });

    // Record the undo/redo action for analytics
    const elementsToRecord = fullElementStates.map((element) => new PlannedBuildingElement(element));
    dispatch(recordUserActions(UserActionType.ELEMENTS_STATUSES_UPDATED, elementsToRecord, camerasController.photoViewerControls));

    // same method updatePlannedBuildingElementsForViewerUndo for both redo and undo
    return Avvir.api.elements.updatePlannedBuildingElementsForViewerUndo({ projectId, floorId }, updatedElements, user);
  }
}
